/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ChipColor, ChipComponent, ChipSize } from '@app/components/chip';
import { PaginatorComponent } from '@app/components/paginator';
import { SelectComponent, SelectOption } from '@app/components/select';
import { TooltipComponent } from '@app/components/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import orderBy from 'lodash/orderBy';

/** Table uses a config to display columns and actions
 *  Example:
    type: 'Challenges',
    columns: [
      { key: 'title', label: 'Name' },
      ...
    ],
    actions: [{
      label: 'Edit',
      action: function,
      condition: if 1 > 2
    }],

    chipColumn input accepts the column key that you want display chips in.
    Labels in tableData should be translation strings.
 */

export enum ColumnType {
  text = 'text',
  chip = 'chip',
  options = 'options',
}

export type SortDirection = 'asc' | 'desc';
export type SortMode = 'client' | 'server';

export interface SortChangeEvent {
  column: string;
  direction: SortDirection | null;
}

export interface TableColumn {
  key: string;
  label: string;
  columnType?: ColumnType;
  chipSize?: ChipSize;
  chipColor?: ChipColor;
  width?: string;
  maxLength?: number;
  sortable?: boolean;
  sortKey?: string;
}

export interface TableConfig {
  type: string;
  columns: TableColumn[];
  sortMode?: SortMode;
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.css',
  standalone: true,
  imports: [
    ChipComponent,
    CommonModule,
    PaginatorComponent,
    SelectComponent,
    TranslateModule,
    TooltipComponent,
  ],
})
export class TableComponent {
  @Output() handleOptionSelected = new EventEmitter<SelectOption>();
  @Output() recordSelected = new EventEmitter<Record<string, any>>();
  @Output() recordUnselected = new EventEmitter<Record<string, any>>();
  @Output() sortChange = new EventEmitter<SortChangeEvent>();

  @Input() config: TableConfig;
  @Input() chipSize = ChipSize.SMALL;
  @Input() chipColor = ChipColor.NEUTRAL;
  @Input() hideOptionIndex: number;
  @Input() rowSelect = false;
  @Input() set page(val: number) {
    this.pageIndex = val;
    this.updateTable();
  }
  @Input() set tableData(val: Array<Record<string, any>>) {
    this.length = 0;
    this.pageIndex = 0;
    this.data = val;
    this.updateTable();
    this.dataReloadCounter++;
  }
  @Input() pageSize = 16;
  @Input() paginate = true;

  elements: Array<Record<string, any>>;
  pageIndex = 0;
  length = 0;
  dataReloadCounter = 0;
  data: Array<Record<string, any>> = [];
  activeSortColumn: string | null = null;
  sortDirection: SortDirection = 'asc';

  readonly options = ColumnType.options;
  readonly chip = ColumnType.chip;

  onPageChanged(page: number): void {
    this.pageIndex = page - 1;
    this.updateTable();
  }

  onOptionSelected(
    option: SelectOption,
    item: Record<string, any>,
    index: number
  ): void {
    this.handleOptionSelected.emit({ ...option, ...item, index });
  }

  onRowSelected(item: Record<string, any>): void {
    item.selected = !item.selected;
    if (item.selected) {
      this.recordSelected.emit(item);
    } else {
      this.recordUnselected.emit(item);
    }
  }

  onSortColumn(column: TableColumn): void {
    if (!column.sortable) return;

    if (this.activeSortColumn === column.key) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else {
        this.activeSortColumn = null;
      }
    } else {
      this.activeSortColumn = column.key;
      this.sortDirection = 'asc';
    }

    const mode = this.config?.sortMode ?? 'client';

    if (mode === 'server') {
      this.sortChange.emit({
        column: column.key,
        direction: this.activeSortColumn ? this.sortDirection : null,
      });
    } else {
      this.updateTable();
    }
  }

  private getSortedData(): Array<Record<string, any>> {
    if (!this.activeSortColumn || this.config?.sortMode === 'server') {
      return this.data;
    }
    const col = this.config?.columns.find(c => c.key === this.activeSortColumn);
    const sortKey = col?.sortKey ?? this.activeSortColumn;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const factor = this.sortDirection === 'asc' ? 1 : -1;
    return [...(this.data ?? [])].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return collator.compare(aVal, bVal) * factor;
      }
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const order = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return order * factor;
    });
  }

  private updateTable(): void {
    const sorted = this.getSortedData();
    this.length = sorted?.length;

    this.elements = [...(sorted ?? [])].splice(
      this.pageIndex * this.pageSize,
      this.pageSize
    );
  }

  getChipSize(rowSize?: ChipSize, configSize?: ChipSize): ChipSize {
    if (rowSize) {
      return rowSize;
    }
    if (configSize) {
      return configSize;
    }
    return this.chipSize;
  }

  getChipColor(rowColor?: ChipColor, configColor?: ChipColor): ChipColor {
    if (rowColor) {
      return rowColor;
    }
    if (configColor) {
      return configColor;
    }
    return this.chipColor;
  }

  getEllipsisText(value: string, maxLength: number): string {
    if (this.showEllipsis(value, maxLength)) {
      return value.substring(0, maxLength) + '...';
    }
    return value;
  }

  showEllipsis(value: string, maxLength: number): boolean {
    return value && maxLength && value.length > maxLength;
  }
}
